import { useEffect, useMemo, useState } from 'react'
import { useLocationStore } from '@/stores/location'
import { useWeather } from '@/lib/useWeather'

// PAGASA heat index thresholds (°C apparent temperature)
export function heatIndexCategory(feelsLikeC: number): { label: string; advisory: string; level: 1 | 2 | 3 | 4 } | null {
  if (feelsLikeC >= 52) return { label: 'Extreme Danger', advisory: 'Stay indoors — heat stroke imminent', level: 4 }
  if (feelsLikeC >= 42) return { label: 'Danger',         advisory: 'Avoid outdoor activities',           level: 3 }
  if (feelsLikeC >= 33) return { label: 'Extreme Caution', advisory: 'Take breaks and stay hydrated',     level: 2 }
  if (feelsLikeC >= 27) return { label: 'Caution',        advisory: 'Limit prolonged outdoor exposure',   level: 1 }
  return null
}

export interface HeatIndexInfo {
  level: 1 | 2 | 3 | 4
  label: string
  advisory: string
  feelsLike: number
}

/**
 * Current heat index for the family's area (first dependent location, else
 * cached GPS coords). Null while loading or when below the Caution threshold.
 */
export function useHeatIndex(): HeatIndexInfo | null {
  const lastSeenMap = useLocationStore((s) => s.lastSeenMap)
  const [gpsLoc, setGpsLoc] = useState<{ lat: number; lng: number } | null>(null)

  const storeLoc = useMemo(() => {
    const entries = Object.values(lastSeenMap)
    if (entries.length > 0) return { lat: entries[0].lat, lng: entries[0].lng }
    try {
      const raw = localStorage.getItem('hazard_gps_coords')
      if (raw) {
        const { lat, lng } = JSON.parse(raw) as { lat: number; lng: number }
        return { lat, lng }
      }
    } catch { /* malformed stored coords */ }
    return null
  }, [lastSeenMap])

  // Store/cache can be empty when the user lands on a screen that never
  // subscribes to locations — fall back to browser geolocation so the
  // sidebar pill works on every screen, not just after visiting Home
  useEffect(() => {
    if (storeLoc || gpsLoc || !navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const l = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        localStorage.setItem('hazard_gps_coords', JSON.stringify(l))
        setGpsLoc(l)
      },
      () => { /* denied/unavailable — pill stays hidden */ },
      { timeout: 8000 },
    )
  }, [storeLoc, gpsLoc])

  const loc = storeLoc ?? gpsLoc
  const weatherLocs = useMemo(
    () => (loc ? [{ label: 'here', lat: loc.lat, lng: loc.lng }] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loc?.lat, loc?.lng],
  )
  const { locations } = useWeather(weatherLocs)

  const feels = locations[0]?.current.feelsLikeC
  if (feels == null) return null
  const cat = heatIndexCategory(feels)
  if (!cat) return null
  return { level: cat.level, label: cat.label, advisory: cat.advisory, feelsLike: Math.round(feels) }
}
